import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";

/**
 * HubSpot connect/status/disconnect API.
 *
 * Talks to the real Express middleware at `/api/hubspot/*`, which proxies to
 * the CRM service (Infynd-CL-CRM-Service). The middleware resolves the user
 * from the session cookie, so no user id is sent from the browser.
 *
 * Unlike `crmApi.ts` (which still points at endpoints — `save_zoho_refresh_token`,
 * `AuthHandler`, etc. — that don't exist on this Express server), this one is
 * wired to a backend that's actually there. It only covers HubSpot; Salesforce
 * and Dynamics still go through the old (currently non-functional) `crmApi`.
 */

export interface HubspotStatus {
  connected: boolean;
  hub_domain: string | null;
  connected_at: string | null;
  /**
   * True once the service has confirmed HubSpot rejected the stored refresh
   * token outright (e.g. the user revoked access) rather than it just losing
   * a race with a concurrent refresh. `connected` stays true in this state —
   * there's still a stored connection — but nothing backed by it will work
   * until the user reconnects.
   */
  needs_reauth: boolean;
}

/**
 * One writable HubSpot property, as the CRM service's columns endpoint
 * returns it.
 *
 * Field names are camelCase here because that is what the service actually
 * sends — its written contract says `field_type`/`group_name`, but the live
 * response carries `fieldType`/`groupName` (verified against a real portal).
 */
export interface HubspotColumn {
  /** HubSpot's internal property name — what gets stored as the mapping value. */
  name: string;
  /** The property's display label, shown in the mapping dropdown. */
  label: string;
  type: string;
  fieldType: string;
  /** HubSpot's own grouping, e.g. "contactinformation". */
  groupName: string;
  calculated: boolean;
  /** Present for enumeration properties; empty for free-text ones. */
  options: { label: string; value: string }[];
}

/**
 * One mappable row on the manage screen.
 *
 * The label, type and required flag come from the service rather than being
 * pinned in the client: which Infynd fields exist is a backend concern, and
 * the previous app's equivalent screen grew well past twenty company fields
 * (NAICS/SIC/NACE codes and friends) without a frontend release each time.
 */
export interface HubspotMappableField {
  /** Infynd's canonical field key — the stable identity of the row. */
  key: string;
  label: string;
  /** Shown as a hint beside the label, e.g. "string". */
  type: string;
  isRequired: boolean;
  /** The chosen HubSpot property's internal name, or null when unmapped. */
  crmFieldValue: string | null;
}

/**
 * Everything the manage screen reads and writes, in one document.
 *
 * Deliberately the same shape the previous Infynd app's
 * `/crm/info/provider/settings` returns, so the service can port that
 * implementation rather than inventing a second contract for the same screen.
 */
export interface HubspotSettings {
  connection: {
    allowContactExport: boolean;
    allowCompanyExport: boolean;
    /** Show "already in HubSpot" badges on records. */
    enableIndicator: boolean;
    /** Skip records that already exist when exporting. */
    excludeFromBulk: boolean;
    /** Fields used to detect an existing record, max 3 per object. */
    indicator: {
      company: HubspotMappableField[];
      contact: HubspotMappableField[];
    };
  };
  fieldMappings: {
    company: HubspotMappableField[];
    contact: HubspotMappableField[];
  };
}

/**
 * One HubSpot list — what HubSpot's own UI calls a segment — as the CRM
 * service's segments endpoint returns it.
 *
 * `matchedRecords`, `status` and `lastSyncedAt` are placeholders the service
 * hardcodes (0 / "not_synced" / null) until the step that reconciles a list
 * against our own database lands in the enrichment service. They are on the
 * contract already so the table does not have to change shape when it does.
 */
export interface HubspotSegment {
  /** The HubSpot list id. */
  value: string;
  /** The list's name, as shown in HubSpot. */
  label: string;
  totalRecords: number;
  /** How many of those we already hold. Always 0 until sync ships. */
  matchedRecords: number;
  /** "not_synced" until sync ships. */
  status: string;
  createdAt: string;
  lastSyncedAt: string | null;
}

export interface HubspotExportResult {
  created: number;
  updated: number;
  results: {
    infynd_id: string;
    hubspot_id?: string;
    status: "created" | "updated" | "skipped" | "error";
    reason?: string;
  }[];
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const hubspotApi = createApi({
  reducerPath: "hubspotApi",
  baseQuery: axiosBaseQuery("/api/hubspot"),
  tagTypes: ["HubspotStatus", "HubspotSettings"],
  endpoints: (builder) => ({
    /** Current user's HubSpot connection, or `connected: false` if there is none. */
    getHubspotStatus: builder.query<HubspotStatus, void>({
      query: () => "/status",
      transformResponse: (response: Envelope<HubspotStatus>) => response.data,
      providesTags: ["HubspotStatus"],
    }),

    /**
     * The HubSpot authorization URL to send the browser to. A mutation rather
     * than a query — it's triggered once, on click, and never cached.
     */
    getHubspotAuthorizeUrl: builder.mutation<{ url: string }, void>({
      query: () => ({ url: "/authorize-url", method: "GET" }),
      transformResponse: (response: Envelope<{ url: string }>) => response.data,
    }),

    disconnectHubspot: builder.mutation<void, void>({
      query: () => ({ url: "/disconnect", method: "POST" }),
      invalidatesTags: ["HubspotStatus"],
    }),

    /**
     * The portal's writable properties for one object, backing every mapping
     * dropdown. Real portals return a lot of these — ~170 for companies and
     * ~290 for contacts on a stock account — so the pickers search rather
     * than scroll.
     */
    getHubspotColumns: builder.query<HubspotColumn[], { objectType: "contacts" | "companies" }>({
      query: ({ objectType }) => `/${objectType}/columns`,
      transformResponse: (response: Envelope<{ columns: HubspotColumn[] }>) =>
        response.data.columns,
    }),

    /**
     * The portal's lists for one object, backing the CRM List tab.
     *
     * Kept out of `getHubspotColumns` even though both are per-object reads:
     * this one hits HubSpot's paginated lists search on every call and is only
     * ever wanted on that one tab, so it stays a separate, separately-skippable
     * query rather than making the mapping tabs pay for it.
     */
    getHubspotSegments: builder.query<HubspotSegment[], { objectType: "contacts" | "companies" }>({
      query: ({ objectType }) => `/${objectType}/segments`,
      transformResponse: (response: Envelope<{ segments: HubspotSegment[] }>) =>
        response.data.segments,
    }),

    /**
     * Field definitions, their saved targets, and the export/duplicate
     * preferences — one document, one save, matching the manage screen.
     */
    getHubspotSettings: builder.query<HubspotSettings, void>({
      query: () => "/settings",
      transformResponse: (response: Envelope<HubspotSettings>) => response.data,
      providesTags: ["HubspotSettings"],
    }),

    saveHubspotSettings: builder.mutation<void, HubspotSettings>({
      query: (body) => ({ url: "/settings", method: "PUT", body }),
      invalidatesTags: ["HubspotSettings"],
    }),

    /**
     * Fills in a default HubSpot property for whatever's still unmapped in
     * the given draft, validated against the portal's real properties.
     * Saves nothing — only saveHubspotSettings above persists anything.
     */
    autoMapHubspotSettings: builder.mutation<HubspotSettings, HubspotSettings>({
      query: (body) => ({ url: "/settings/auto-map", method: "POST", body }),
      transformResponse: (response: Envelope<HubspotSettings>) => response.data,
    }),

    exportToHubspot: builder.mutation<
      HubspotExportResult,
      { objectType: "contacts" | "companies"; selectedIds: string[] }
    >({
      query: ({ objectType, selectedIds }) => ({
        url: `/export/${objectType}`,
        method: "POST",
        body: { selectedIds },
      }),
      transformResponse: (response: Envelope<HubspotExportResult>) => response.data,
    }),
  }),
});

export const {
  useGetHubspotStatusQuery,
  useGetHubspotAuthorizeUrlMutation,
  useDisconnectHubspotMutation,
  useGetHubspotColumnsQuery,
  useGetHubspotSegmentsQuery,
  useGetHubspotSettingsQuery,
  useSaveHubspotSettingsMutation,
  useAutoMapHubspotSettingsMutation,
  useExportToHubspotMutation,
} = hubspotApi;

export default hubspotApi;
